import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reset-password — takes a token from the URL, accepts a new password (with
 * confirmation), and on success logs the user in and redirects to dashboard.
 * Backend already sets the auth cookies in its response.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const mismatch = confirm.length > 0 && pw !== confirm;
  const tooShort = pw.length > 0 && pw.length < 8;
  const canSubmit = useMemo(
    () => token.length > 20 && pw.length >= 8 && pw === confirm,
    [token, pw, confirm]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true); setErr("");
    try {
      await axios.post(`${API}/auth/reset-password`,
        { token, new_password: pw },
        { withCredentials: true });
      // Backend set auth cookies; refresh AuthContext so /app/dashboard sees us logged in.
      await refresh();
      toast.success("Password updated. You're signed in.");
      navigate("/app/dashboard", { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Invalid / missing token state — surface immediately, don't even show form.
  if (!token || token.length < 20) {
    return (
      <div className="min-h-screen bg-bg flex flex-col">
        <div className="px-6 md:px-12 py-6 border-b border-line">
          <Link to="/" className="flex items-center w-fit">
            <BrandLogo variant="wordmark" size={28} showLink={false} />
          </Link>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md surface p-6" data-testid="reset-token-missing">
            <h1 className="font-heading text-2xl uppercase tracking-tight mb-3">
              Reset link incomplete
            </h1>
            <p className="text-ink-muted text-sm leading-relaxed mb-4">
              This page needs a valid reset token in the URL. Make sure you clicked the link in your reset email directly, not a copy-paste that lost the token.
            </p>
            <Link to="/forgot-password" className="btn-volt inline-block" data-testid="reset-request-new">
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <div className="px-6 md:px-12 py-6 border-b border-line">
        <Link to="/" className="flex items-center w-fit">
          <BrandLogo variant="wordmark" size={28} showLink={false} />
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md" data-testid="reset-password-page">
          <div className="label-eyebrow mb-3">Choose a new password</div>
          <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-3">
            Set new password
          </h1>
          <p className="text-ink-muted text-sm leading-relaxed mb-8">
            Pick something you'll remember. Minimum 8 characters — longer is safer.
          </p>
          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block">
              <div className="label-eyebrow mb-2">New password</div>
              <input
                type="password" required minLength={8}
                value={pw} onChange={(e) => setPw(e.target.value)}
                placeholder="At least 8 characters"
                autoFocus autoComplete="new-password"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white"
                data-testid="reset-password-input"
              />
              {tooShort && (
                <div className="text-xs text-[#FF3B30] mt-1.5">Must be at least 8 characters.</div>
              )}
            </label>
            <label className="block">
              <div className="label-eyebrow mb-2">Confirm password</div>
              <input
                type="password" required minLength={8}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="Type it again"
                autoComplete="new-password"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white"
                data-testid="reset-password-confirm-input"
              />
              {mismatch && (
                <div className="text-xs text-[#FF3B30] mt-1.5">Passwords don't match.</div>
              )}
            </label>
            {err && <div className="text-sm text-[#FF3B30]" data-testid="reset-error">{err}</div>}
            <button type="submit" disabled={!canSubmit || submitting}
              className="w-full btn-volt disabled:opacity-50"
              data-testid="reset-submit-btn">
              {submitting ? "Updating…" : "Update password & sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
